import microuniversoApi from "app/api/microuniversoApi";
import store from "app/redux/store/store";
import * as permissoesActions from 'app/redux/permissoesSlice';
import globalMU from "globalMU";

// Cache local para resultados
const permissionCache = new Map<string, { value: boolean; timestamp: number }>();

// Cache para promessas pendentes
const pendingRequests = new Map<string, Promise<boolean>>();

// Tempo de validade do cache (5 minutos)
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; 

export type ObterPermissaoInput = {
    fazerCache?: boolean;
    usarCacheSePossivel?: boolean;
    funcionalidade?: string;
    acao?: string;
};

export async function obterPermissao({
    fazerCache = true,
    usarCacheSePossivel = true,
    funcionalidade,
    acao
}: ObterPermissaoInput): Promise<boolean> {
    if (!funcionalidade) {
        throw new Error("funcionalidade é obrigatória");
    }

    const chave = `${funcionalidade}` + (globalMU.isNotNullOrEmpty(acao) ? `;${acao}` : '');
    
    // 1. Verificar cache local primeiro
    const now = Date.now();
    const cached = permissionCache.get(chave);
    
    if (usarCacheSePossivel && cached && (now - cached.timestamp) < CACHE_EXPIRATION_MS) {
        return cached.value;
    }

    // 2. Verificar se já existe uma requisição pendente
    if (pendingRequests.has(chave)) {
        return pendingRequests.get(chave)!;
    }

    // 3. Verificar cache do Redux se válido
    const permissoesRedux = store.getState().permissoes;
    
    if (usarCacheSePossivel && chave in permissoesRedux) {
        const reduxValue = permissoesRedux[chave];
        
        // Atualizar cache local
        permissionCache.set(chave, { value: reduxValue, timestamp: now });
        return reduxValue;
    }

    // 4. Criar nova requisição
    const requestPromise = (async () => {
        try {
            const permitido = await microuniversoApi.PermissaoFuncionalidade.obterUsuarioPermitidoFuncionalidadeAcao(
                funcionalidade, 
                [acao]
            );

            // Atualizar cache local
            permissionCache.set(chave, { value: permitido, timestamp: Date.now() });

            // Atualizar Redux se necessário
            if (fazerCache) {
                store.dispatch(permissoesActions.inserirOuAtualizar({
                    funcionalidade,
                    acao,
                    permitido
                }));
            }

            return permitido;
        } finally {
            // Remover da lista de pendentes
            pendingRequests.delete(chave);
        }
    })();

    // Registrar promessa pendente
    pendingRequests.set(chave, requestPromise);

    return requestPromise;
}